import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useConfirm } from 'shared/hooks';
import { partnerUserAlerts, useAlert } from 'shared/components/alerts';
import { hqRoutes } from 'hq/Routes';
import { QUERY_KEYS } from 'api/query-keys';
import { useDeletePartnerUser } from './use-delete-partner-user';
import { useHqRelativePath } from '../../../../hooks/useHqRelativePath';

const useArchivePartnerUserDialog = (partnerId: string) => {
  const [partnerUserId, setPartnerUserId] = useState<string>('');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const generatePath = useHqRelativePath();
  const { t } = useTranslation(['hq-screen-specific', 'web-common']);
  const { alertSuccess, alertError } = useAlert();
  const archivePartnerUser = useDeletePartnerUser();

  function handleArchivePartnerUser() {
    archivePartnerUser.mutate(partnerUserId, {
      onSuccess: () => {
        dialog.close();
        queryClient.invalidateQueries(QUERY_KEYS.partners());
        alertSuccess(partnerUserAlerts.success.deleted);
        navigate(generatePath(hqRoutes.PartnerUsers, { id: partnerId }));
      },
      onError: (e) => alertError(e),
    });
  }

  const dialog = useConfirm({
    title: t('partners.dialogs.archive_partner_user.title', { ns: 'hq-screen-specific' }),
    positiveAction: {
      label: t('buttons.yes', { ns: 'web-common' }),
      action: handleArchivePartnerUser,
    },
    negativeAction: { label: t('buttons.no', { ns: 'web-common' }), autoFocus: true },
  });

  function open(id: string) {
    setPartnerUserId(id);
    dialog.open();
  }

  return { ...dialog, open };
};

export { useArchivePartnerUserDialog };
